import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
    name: "", cuisine: "", description: "", image_url: "", address: "",
    lat: 37.78, lng: -122.41, price_range: "$$", opens_at: "11:00", closes_at: "23:00", tax_rate: 0.085,
};

export default function AdminRestaurantsPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/restaurants");
            setItems(data || []);
        } catch (error) {
            console.warn("[admin-restaurants] load failed", error?.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
    const startEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };
    const remove = async (r) => {
        if (!window.confirm(`Delete ${r.name}?`)) return;
        try {
            await api.delete(`/admin/restaurants/${r.id}`);
            toast.success("Deleted");
            load();
        } catch (error) {
            console.warn("[admin-restaurants] delete failed", error?.message);
            toast.error("Could not delete");
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            if (editing) {
                await api.patch(`/admin/restaurants/${editing.id}`, form);
            } else {
                await api.post("/admin/restaurants", form);
            }
            toast.success("Saved");
            setOpen(false);
            load();
        } catch (error) {
            console.warn("[admin-restaurants] save failed", error?.message);
            toast.error(error?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl">Restaurants</h1>
                    <p className="text-sm text-muted-foreground">Add, edit, or remove restaurants.</p>
                </div>
                <Button onClick={startCreate} data-testid="admin-create-button"><Plus className="h-4 w-4 mr-1" /> New restaurant</Button>
            </div>
            {loading ? <Skeleton className="h-72" /> : (
                <Card><CardContent className="p-0 overflow-x-auto">
                    <Table data-testid="admin-restaurants-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Cuisine</TableHead>
                                <TableHead>Address</TableHead>
                                <TableHead>Price</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map(r => (
                                <TableRow key={r.id} className="cursor-pointer hover:bg-accent/30" onClick={() => navigate(`/admin/restaurants/${r.id}`)} data-testid="admin-restaurant-row">
                                    <TableCell><div className="font-medium">{r.name}</div></TableCell>
                                    <TableCell>{r.cuisine}</TableCell>
                                    <TableCell className="max-w-xs truncate">{r.address}</TableCell>
                                    <TableCell>{r.price_range}</TableCell>
                                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                        <Button variant="ghost" size="sm" onClick={() => startEdit(r)} data-testid="admin-edit-button"><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid="admin-delete-button" className="text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!items.length && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No restaurants yet</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </CardContent></Card>
            )}

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
                    <SheetHeader><SheetTitle>{editing ? "Edit" : "New"} restaurant</SheetTitle></SheetHeader>
                    <div className="space-y-3 mt-4">
                        <FormRow label="Name"><Input value={form.name} onChange={e => update("name", e.target.value)} data-testid="admin-form-name" /></FormRow>
                        <FormRow label="Cuisine"><Input value={form.cuisine} onChange={e => update("cuisine", e.target.value)} /></FormRow>
                        <FormRow label="Description"><Textarea value={form.description} onChange={e => update("description", e.target.value)} /></FormRow>
                        <FormRow label="Image URL"><Input value={form.image_url} onChange={e => update("image_url", e.target.value)} /></FormRow>
                        <FormRow label="Address"><Input value={form.address} onChange={e => update("address", e.target.value)} /></FormRow>
                        <div className="grid grid-cols-2 gap-3">
                            <FormRow label="Lat"><Input type="number" step="0.0001" value={form.lat} onChange={e => update("lat", parseFloat(e.target.value))} /></FormRow>
                            <FormRow label="Lng"><Input type="number" step="0.0001" value={form.lng} onChange={e => update("lng", parseFloat(e.target.value))} /></FormRow>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <FormRow label="Price">
                                <Select value={form.price_range} onValueChange={(v) => update("price_range", v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{["$", "$$", "$$$", "$$$$"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                </Select>
                            </FormRow>
                            <FormRow label="Opens"><Input value={form.opens_at} onChange={e => update("opens_at", e.target.value)} /></FormRow>
                            <FormRow label="Closes"><Input value={form.closes_at} onChange={e => update("closes_at", e.target.value)} /></FormRow>
                        </div>
                        <FormRow label="Tax rate (decimal)"><Input type="number" step="0.001" value={form.tax_rate} onChange={e => update("tax_rate", parseFloat(e.target.value))} /></FormRow>
                    </div>
                    <SheetFooter className="mt-6">
                        <Button onClick={save} disabled={saving} data-testid="admin-save-button">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>
        </div>
    );
}

function FormRow({ label, children }) {
    return <div className="grid gap-1"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>;
}
